/**
 * HostScreenView — displays the host's shared screen (their music player) to
 * all players during scoring and revealing phases.
 *
 * Replaces the YouTubePlayer component. Receives the MediaStream directly from
 * WebRTCContext (localScreenStream for host, or remote screen stream for others).
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function HostScreenView({ stream, isHost, onRequestShare, onStall, fill = false }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const retryCountRef = useRef(0);
  const healthTimerRef = useRef(null);
  const pendingAutoplayCleanupRef = useRef(null);
  const onStallRef = useRef(onStall);
  useEffect(() => { onStallRef.current = onStall; }, [onStall]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Guard: skip re-attachment when WebRTCContext recreates a MediaStream wrapper
    // around the same underlying video track (happens on every useTracks fire, e.g.
    // when a mic is published/subscribed). Re-attaching srcObject causes the h-auto
    // video element to briefly lose its intrinsic dimensions → layout shift → grid jump.
    if (stream && el.srcObject instanceof MediaStream) {
      const incomingVideoTrack = stream.getVideoTracks()[0];
      const currentVideoTrack = el.srcObject.getVideoTracks()[0];
      if (incomingVideoTrack && currentVideoTrack && incomingVideoTrack.id === currentVideoTrack.id) {
        return;
      }
    }

    retryCountRef.current = 0;
    clearTimeout(healthTimerRef.current);

    pendingAutoplayCleanupRef.current?.();
    pendingAutoplayCleanupRef.current = null;

    el.srcObject = stream || null;
    if (stream) {
      el.play().catch(() => {
        const resume = () => {
          pendingAutoplayCleanupRef.current?.();
          pendingAutoplayCleanupRef.current = null;
          if (el.srcObject !== stream) return;
          el.play().catch(() => {});
        };
        // Listen on the element itself (native play-button taps may not bubble)
        // and on document (any other user interaction).
        el.addEventListener('click', resume, { once: true });
        document.addEventListener('click', resume, { once: true });
        document.addEventListener('touchstart', resume, { once: true });
        pendingAutoplayCleanupRef.current = () => {
          el.removeEventListener('click', resume);
          document.removeEventListener('click', resume);
          document.removeEventListener('touchstart', resume);
        };
      });

      // Health check for non-host viewers: detect black screen share
      if (!isHost) {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          scheduleHealthCheck(el, stream, videoTrack);
        }
      }
    }

    return () => {
      clearTimeout(healthTimerRef.current);
      pendingAutoplayCleanupRef.current?.();
      pendingAutoplayCleanupRef.current = null;
    };
  }, [stream, isHost]); // eslint-disable-line react-hooks/exhaustive-deps

  function scheduleHealthCheck(el, stream, videoTrack) {
    const delay = retryCountRef.current === 0 ? 2500 : 1500;
    healthTimerRef.current = setTimeout(() => {
      if (!el || el.srcObject !== stream) return;
      if (videoTrack.readyState !== 'live' || videoTrack.muted) return;
      if (el.videoWidth > 0) return;

      const attempt = ++retryCountRef.current;
      if (attempt === 1) {
        el.srcObject = null;
        el.srcObject = stream;
        el.play().catch(() => {});
        scheduleHealthCheck(el, stream, videoTrack);
      } else if (attempt === 2) {
        onStallRef.current?.(2);
      } else if (attempt === 3) {
        onStallRef.current?.(3);
      }
    }, delay);
  }

  if (!stream) {
    return (
      <div className={fill ? 'flex flex-col items-center justify-center h-full' : 'flex justify-center'}>
        <div className="t-bg-surface t-border border rounded-xl px-5 py-3 text-center space-y-2 inline-flex flex-col items-center">
          <p className="text-sm t-text-muted">
            {isHost ? t('screenShare.hostNotSharing') : t('screenShare.waitingForHost')}
          </p>
          {isHost && onRequestShare && (
            <button
              type="button"
              onClick={onRequestShare}
              className="t-btn-primary font-semibold rounded-lg px-4 py-2 text-sm transition-colors t-focus-ring"
            >
              {t('screenShare.shareButton')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={fill ? 'h-full flex flex-col' : 'flex justify-center'}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isHost}
        className={fill ? 'flex-1 min-h-0 w-full rounded-xl' : 'w-full h-auto rounded-xl'}
        style={fill ? { objectFit: 'contain' } : { maxHeight: '25vh', objectFit: 'contain', display: 'block' }}
        aria-label={t('screenShare.videoLabel')}
      />
    </div>
  );
}
